import React from "react";
import { useSearchParams } from "react-router-dom";
import { Printer, FileText } from "lucide-react";

/**
 * Floating no-print toggle that lets the user choose between
 *   • "With letter-head" (full digital header from PrintHeader)
 *   • "Without header" (letterhead mode — header removed, top spacer added)
 * Writes the choice into the URL (?letterhead=1) so refreshing the print preview keeps the state.
 *
 * Usage in a print page:
 *   const letterhead = useLetterheadMode();          // boolean
 *   {!letterhead && <PrintHeader ... />}
 *   <LetterheadToggle />                              // renders floating button (hidden on print)
 */

export function useLetterheadMode() {
  const [params] = useSearchParams();
  return params.get("letterhead") === "1";
}

export default function LetterheadToggle() {
  const [params, setParams] = useSearchParams();
  const on = params.get("letterhead") === "1";

  const toggle = () => {
    const next = new URLSearchParams(params);
    if (on) next.delete("letterhead");
    else next.set("letterhead", "1");
    // suppress the auto-print effect after toggling so user can decide
    next.set("autoprint", "0");
    setParams(next, { replace: true });
  };

  return (
    <div className="no-print fixed top-3 right-3 z-50 flex items-center gap-2 rounded-full border border-border bg-white/95 px-2 py-1 shadow-md backdrop-blur" data-testid="letterhead-toggle">
      <button
        onClick={() => { const n = new URLSearchParams(params); n.delete("letterhead"); setParams(n, { replace: true }); }}
        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${!on ? "bg-brand text-white" : "text-ink-muted hover:bg-secondary"}`}
        data-testid="letterhead-toggle-with"
        title="Print with full digital header (logo + name + GSTIN + branch)"
      >
        <Printer size={11} /> With header
      </button>
      <button
        onClick={() => { const n = new URLSearchParams(params); n.set("letterhead", "1"); setParams(n, { replace: true }); }}
        className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${on ? "bg-brand text-white" : "text-ink-muted hover:bg-secondary"}`}
        data-testid="letterhead-toggle-without"
        title="Print without header (use your pre-printed letterhead)"
      >
        <FileText size={11} /> On letterhead
      </button>
      <button
        onClick={() => window.print()}
        className="ml-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
        data-testid="letterhead-print-btn"
      >Print</button>
      {/* Suppress the toggle button itself + add a tall top reserve only when in letterhead mode */}
      {on && <style>{`@page { margin-top: 55mm; }`}</style>}
      {/* note: 55mm ≈ pre-printed letterhead area; adjust per hospital if needed */}
    </div>
  );
}
